import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@app/store';
import { fetchProcessRoles, deleteProcessRole } from './processRoleSlice';
import { DataTable, DataFilter } from '@components/ui';
import type { Column } from '@components/ui/DataTable';
import { ProcessRoleOutputDTO } from '@types';
import { LoadingSkeleton } from '@components/ui/LoadingSkeleton';
import { ErrorDisplay } from '@components/ui/ErrorDisplay';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';

const ProcessRoleList = () => {
  console.log('🔄 Renderizando ProcessRoleList');
  
  const dispatch = useDispatch<AppDispatch>();
  const { items, loading, error } = useSelector((state: RootState) => {
    console.log('🔍 Selector executando, loading:', state.processRole.loading);
    return state.processRole;
  });
  
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  const loadData = useCallback(async (currentFilters: Record<string, string>) => {
    // Evita chamadas simultâneas
    if (isLoadingRef.current) {
      console.log('🚫 Requisição em andamento, ignorando nova chamada');
      return;
    }

    // Evita chamadas após o carregamento inicial
    if (initialLoadDoneRef.current && Object.keys(currentFilters).length === 0) {
      console.log('🚫 Ignorando chamada sem filtros após carregamento inicial');
      return;
    }

    console.log('📡 Iniciando requisição fetchProcessRoles');
    isLoadingRef.current = true;

    try {
      await dispatch(fetchProcessRoles(currentFilters));
      console.log('✅ Requisição fetchProcessRoles completada');
      setIsInitialLoad(false);
      initialLoadDoneRef.current = true;
    } catch (error) {
      console.error('❌ Erro na requisição:', error);
    } finally {
      isLoadingRef.current = false;
    }
  }, [dispatch]);

  const handleFilter = useCallback((newFilters: Record<string, string>) => {
    console.log('🔍 Novos filtros aplicados:', newFilters);
    setFilters(newFilters);
  }, []);

  // Efeito único para carregamento inicial
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      loadData({});
    }
  }, [loadData]);

  // Efeito separado para mudanças nos filtros
  useEffect(() => {
    if (initialLoadDoneRef.current && Object.keys(filters).length > 0) {
      loadData(filters);
    }
  }, [filters, loadData]);

  const handleDelete = async (id: string) => {
    console.log('🗑️ Iniciando delete do item:', id);
    try {
      await dispatch(deleteProcessRole(id));
      setDeleteId(null);
      console.log('🔄 Recarregando lista após delete');
      dispatch(fetchProcessRoles(filters));
    } catch (error) {
      console.error('❌ Erro ao excluir:', error);
    }
  };

  const filterFields = [
    {
      name: 'name',
      label: 'Nome',
      type: 'text' as const,
    },
    {
      name: 'description',
      label: 'Descrição',
      type: 'text' as const,
    }
  ];

  const columns: Column<ProcessRoleOutputDTO>[] = [
    {
      key: 'name',
      header: 'Nome',
    },
    {
      key: 'description',
      header: 'Descrição',
    },
    {
      key: 'additionalData',
      header: 'Dados Adicionais',
      render: (value: ProcessRoleOutputDTO['additionalData']) => {
        return value?.length
          ? `${value.length} campo(s)`
          : 'Nenhum';
      }
    }
  ];

  if (isInitialLoad && loading) {
    return <LoadingSkeleton />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Personagens
      </Typography>

      {error && <ErrorDisplay error={error} />}

      <DataFilter
        fields={filterFields}
        onFilter={handleFilter}
        autoSubmit={true}
        debounceTime={500}
      />

      <DataTable<ProcessRoleOutputDTO>
        data={items}
        columns={columns}
        basePath="process-roles"
        onDelete={(id) => {
          console.log('🗑️ Solicitação de delete para id:', id);
          setDeleteId(id);
        }}
        loading={loading}
        error={error}
        loadingComponent={<LoadingSkeleton title={false} filter={false} />}
      />

      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este tipo de bem?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancelar</Button>
          <Button
            onClick={() => deleteId && handleDelete(deleteId)}
            color="error"
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessRoleList; 
